import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import MainLayout from '../components/MainLayout';
import axios from 'axios';
import { toast } from 'sonner';
import { Download, Loader2, FileBarChart2, BarChart3 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const REPORTS = [
  { key: 'pending', label: 'Pending Approval', endpoint: '/approval-reports/pending' },
  { key: 'module-wise', label: 'Module-wise', endpoint: '/approval-reports/module-wise' },
  { key: 'manager-wise', label: 'Manager-wise Pending', endpoint: '/approval-reports/manager-wise' },
  { key: 'employee-status', label: 'Employee Request Status', endpoint: '/approval-reports/employee-status' },
  { key: 'ageing', label: 'Approval Ageing', endpoint: '/approval-reports/ageing' },
  { key: 'rejected-sent-back', label: 'Rejected / Sent Back', endpoint: '/approval-reports/rejected-sent-back' },
];

const ROW_COLS = [
  ['module_label', 'Module'], ['employee_code', 'Code'], ['employee_name', 'Employee'],
  ['department', 'Department'], ['manager_name', 'Reporting Manager'],
  ['display_status', 'Status'], ['amount', 'Amount'], ['period', 'Period'],
  ['created_at', 'Requested'], ['ageing_days', 'Ageing (d)'], ['remarks', 'Remarks'],
];

export default function ApprovalReports() {
  const navigate = useNavigate();
  const [active, setActive] = useState('pending');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  const report = REPORTS.find((r) => r.key === active);

  const fetchReport = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (active === 'employee-status' && search) params.search = search;
      const res = await axios.get(`${API_URL}${report.endpoint}`, { params, withCredentials: true });
      setData(res.data);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to load report'); setData(null); }
    setLoading(false);
  }, [active, search, report.endpoint]);

  useEffect(() => { fetchReport(); }, [fetchReport]);

  const downloadCsv = () => {
    window.open(`${API_URL}${report.endpoint}?format=csv`, '_blank');
  };

  const rows = data?.rows || [];

  return (
    <MainLayout>
      <div data-testid="approval-reports-page" className="space-y-5">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-xl font-bold text-slate-900 flex items-center gap-2">
              <FileBarChart2 className="w-5 h-5 text-[#0052FF]" /> Approval Reports
            </h1>
            <p className="text-xs text-slate-500 mt-0.5">
              Cross-module view of every ESS request — advances, claims, regularization, leave and more.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              data-testid="go-to-consolidated-report-btn"
              onClick={() => navigate('/manager/approval-report')}
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-semibold bg-[#0052FF] text-white hover:bg-blue-600 shadow-xs transition"
            >
              <BarChart3 className="w-3.5 h-3.5" /> Consolidated Manager Report
            </button>
            <button data-testid="report-download-csv" onClick={downloadCsv}
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-semibold bg-slate-900 text-white hover:bg-slate-700">
              <Download className="w-3.5 h-3.5" /> Download CSV
            </button>
          </div>
        </div>

        <div className="flex flex-wrap gap-1.5 items-center">
          {REPORTS.map((r) => (
            <button key={r.key} data-testid={`report-tab-${r.key}`}
              onClick={() => setActive(r.key)}
              className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition ${
                active === r.key
                  ? 'bg-[#0052FF] text-white border-[#0052FF]'
                  : 'bg-white text-slate-600 border-slate-200 hover:border-slate-400'
              }`}>
              {r.label}
            </button>
          ))}
          <button
            data-testid="report-tab-consolidated-manager"
            onClick={() => navigate('/manager/approval-report')}
            className="px-3 py-1.5 rounded-full text-xs font-semibold border border-blue-200 bg-blue-50 text-[#0052FF] hover:bg-blue-100 flex items-center gap-1 transition"
          >
            <BarChart3 className="w-3 h-3" /> Consolidated Manager Report &rarr;
          </button>
        </div>

        {active === 'employee-status' && (
          <input data-testid="report-search" value={search} onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by employee name or code…"
            className="w-full max-w-sm border-2 border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#0052FF]" />
        )}

        {/* Ageing buckets */}
        {active === 'ageing' && data?.buckets && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="ageing-buckets">
            {Object.entries(data.buckets).map(([k, v]) => (
              <div key={k} className="bg-white rounded-xl border border-slate-200 p-4 text-center">
                <p className="text-xs text-slate-500">{k}</p>
                <p className={`text-2xl font-bold mt-1 ${k === '15+ days' && v > 0 ? 'text-red-600' : 'text-slate-900'}`}>{v}</p>
              </div>
            ))}
          </div>
        )}

        {loading ? (
          <div className="flex items-center justify-center py-16 text-slate-400">
            <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading report…
          </div>
        ) : active === 'module-wise' ? (
          <div className="bg-white rounded-xl border border-slate-200 overflow-x-auto">
            <table className="w-full text-left">
              <thead>
                <tr className="border-b border-slate-100 text-[11px] uppercase tracking-wide text-slate-400">
                  <th className="px-4 py-2.5">Module</th><th className="px-4 py-2.5">Pending</th>
                  <th className="px-4 py-2.5">Approved</th><th className="px-4 py-2.5">Rejected</th>
                  <th className="px-4 py-2.5">Sent Back</th><th className="px-4 py-2.5">Total</th>
                </tr>
              </thead>
              <tbody>
                {(data?.modules || []).map((m) => (
                  <tr key={m.module} className="border-b border-slate-50 last:border-0" data-testid={`module-row-${m.module}`}>
                    <td className="px-4 py-3 text-xs font-semibold text-slate-800">{m.module_label}</td>
                    <td className="px-4 py-3 text-xs text-amber-700 font-semibold">{m.pending}</td>
                    <td className="px-4 py-3 text-xs text-emerald-700">{m.approved}</td>
                    <td className="px-4 py-3 text-xs text-red-600">{m.rejected}</td>
                    <td className="px-4 py-3 text-xs text-purple-600">{m.sent_back}</td>
                    <td className="px-4 py-3 text-xs font-bold text-slate-900">{m.total}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : active === 'manager-wise' ? (
          <div className="bg-white rounded-xl border border-slate-200 overflow-x-auto">
            <table className="w-full text-left">
              <thead>
                <tr className="border-b border-slate-100 text-[11px] uppercase tracking-wide text-slate-400">
                  <th className="px-4 py-2.5">Reporting Manager</th><th className="px-4 py-2.5">Email</th>
                  <th className="px-4 py-2.5">Pending</th><th className="px-4 py-2.5">Oldest (days)</th>
                  <th className="px-4 py-2.5">Modules</th>
                </tr>
              </thead>
              <tbody>
                {(data?.managers || []).map((m, i) => (
                  <tr key={i} className="border-b border-slate-50 last:border-0" data-testid={`manager-row-${i}`}>
                    <td className="px-4 py-3 text-xs font-semibold text-slate-800">{m.manager_name}</td>
                    <td className="px-4 py-3 text-xs text-slate-500">{m.manager_email || '—'}</td>
                    <td className="px-4 py-3 text-xs font-bold text-amber-700">{m.pending}</td>
                    <td className="px-4 py-3 text-xs text-slate-600">{m.oldest_ageing}</td>
                    <td className="px-4 py-3 text-[11px] text-slate-500">
                      {Object.entries(m.modules || {}).map(([k, v]) => `${k}: ${v}`).join(' · ')}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="bg-white rounded-xl border border-slate-200 overflow-x-auto">
            {rows.length === 0 ? (
              <p className="text-sm text-slate-400 text-center py-10" data-testid="report-empty">No records</p>
            ) : (
              <table className="w-full text-left">
                <thead>
                  <tr className="border-b border-slate-100 text-[11px] uppercase tracking-wide text-slate-400">
                    {ROW_COLS.map(([k, label]) => <th key={k} className="px-3 py-2.5 whitespace-nowrap">{label}</th>)}
                  </tr>
                </thead>
                <tbody>
                  {rows.slice(0, 500).map((r, i) => (
                    <tr key={i} className="border-b border-slate-50 last:border-0" data-testid={`report-row-${i}`}>
                      {ROW_COLS.map(([k]) => (
                        <td key={k} className="px-3 py-2.5 text-xs text-slate-700 whitespace-nowrap max-w-[200px] truncate">
                          {k === 'created_at' ? (r[k] || '').slice(0, 10)
                            : k === 'amount' ? (r[k] != null ? `₹${Number(r[k]).toLocaleString('en-IN')}` : '—')
                              : (r[k] ?? '—') || '—'}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}
      </div>
    </MainLayout>
  );
}
